<div class="min-vh-100 bg-light">
  <app-header [page]="'Admin'"></app-header>

  <main class="container py-4">
    <div class="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
      <div>
        <h4 class="fw-bold mb-0">Currency Payment Gateway Mappings</h4>
        <div class="text-muted small">Configure deposit channels and the internal withdrawal route for each currency.</div>
      </div>
      <button class="btn btn-outline-secondary btn-sm" type="button" (click)="loadMappings()">Refresh</button>
    </div>

    <div class="card shadow-sm border-0 mb-4">
      <div class="card-body">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <div class="fw-semibold">{{ editingId ? 'Edit Mapping' : 'Create Mapping' }}</div>
          <button *ngIf="editingId" class="btn btn-outline-secondary btn-sm" type="button" (click)="cancelEdit()">
            Cancel Edit
          </button>
        </div>

        <form [formGroup]="form" (ngSubmit)="submit()" class="row g-3">
          <div class="col-md-3">
            <label class="form-label">Currency</label>
            <select class="form-select" formControlName="currencyCode">
              <option value="">Select currency</option>
              <option *ngFor="let c of currencies" [value]="c.currencyCode">
                {{ c.currencyName }} ({{ c.currencyCode }})
              </option>
            </select>
          </div>

          <div class="col-md-5">
            <label class="form-label">Gateway Config</label>
            <select class="form-select" formControlName="gatewayConfigId">
              <option value="">Select config</option>
              <option *ngFor="let c of configs" [value]="c.id">
                {{ c.name }} - {{ c.providerName }} - {{ c.environment }}
              </option>
            </select>
          </div>

          <div class="col-md-2">
            <label class="form-label">Workflow</label>
            <select class="form-select" formControlName="workflow">
              <option *ngFor="let item of workflowOptions" [value]="item">{{ item }}</option>
            </select>
          </div>

          <div class="col-md-2">
            <label class="form-label">Priority</label>
            <input class="form-control" type="number" min="1" formControlName="priority">
          </div>

          <div class="col-md-3">
            <label class="form-label">Country Code</label>
            <select class="form-select" formControlName="countryCode">
              <option value="">Any eligible country</option>
              <option *ngFor="let p of countries" [value]="p.code">{{ p.name }} ({{ p.code }})</option>
            </select>
          </div>

          <div class="col-md-3">
            <label class="form-label">Payment Method</label>
            <select class="form-select" formControlName="paymentMethod" (change)="onPaymentMethodChanged()">
              <option *ngFor="let item of paymentMethodOptions" [value]="item.value">{{ item.label }}</option>
            </select>
          </div>

          <div class="col-md-3">
            <label class="form-label">Channel Type</label>
            <select class="form-select" formControlName="channelType">
              <option *ngFor="let item of channelTypeOptions" [value]="item.value">{{ item.label }}</option>
            </select>
            <div class="form-text">Deposit channels use this to decide whether to redirect, show instructions, or show a reusable account.</div>
          </div>

          <div class="col-md-3">
            <label class="form-label">Frontend Hint</label>
            <select class="form-select" formControlName="frontendHint">
              <option *ngFor="let item of frontendHintOptions" [value]="item">{{ item }}</option>
            </select>
          </div>

          <div class="col-md-4">
            <label class="form-label">Customer-facing Name</label>
            <input class="form-control" formControlName="displayName" placeholder="Pay Online">
            <div class="form-text">Shown to the user on the deposit page.</div>
          </div>

          <div class="col-md-8">
            <label class="form-label">Customer-facing Description</label>
            <input class="form-control" formControlName="description" placeholder="Pay securely with card or other available online methods.">
          </div>

          <div class="col-md-4">
            <label class="form-label">Provider Payment Option</label>
            <input class="form-control" formControlName="providerPaymentOption" placeholder="card,banktransfer">
            <div class="form-text">Optional provider-specific option, for example Flutterwave payment_options.</div>
          </div>

          <div class="col-md-2">
            <label class="form-label">Min Amount</label>
            <input class="form-control" type="number" formControlName="minAmount">
          </div>

          <div class="col-md-2">
            <label class="form-label">Max Amount</label>
            <input class="form-control" type="number" formControlName="maxAmount">
          </div>

          <div class="col-md-2">
            <label class="form-label">Customer Type</label>
            <input class="form-control" formControlName="customerType" placeholder="Individual">
          </div>

          <div class="col-md-2 d-flex align-items-end">
            <div class="form-check mb-2">
              <input class="form-check-input" type="checkbox" formControlName="requireBvn" id="requireBvn">
              <label class="form-check-label" for="requireBvn">Require BVN</label>
            </div>
          </div>

          <div class="col-md-2 d-flex align-items-end">
            <div class="form-check mb-2">
              <input class="form-check-input" type="checkbox" formControlName="isDefault" id="isDefault">
              <label class="form-check-label" for="isDefault">Default</label>
            </div>
          </div>

          <div class="col-md-2 d-flex align-items-end">
            <div class="form-check mb-2">
              <input class="form-check-input" type="checkbox" formControlName="isActive" id="isActive">
              <label class="form-check-label" for="isActive">Active</label>
            </div>
          </div>

          <div class="col-12">
            <label class="form-label">Advanced Metadata JSON</label>
            <textarea class="form-control font-monospace" rows="3" formControlName="metadataJson"
              placeholder='{"providerPaymentMethod":"...","providerWalletId":"..."}'></textarea>
            <div class="form-text">
              Existing advanced properties are preserved. The customer-facing fields above are merged into this metadata when you save.
            </div>
          </div>

          <div class="col-12">
            <button class="btn btn-primary" type="submit" [disabled]="form.invalid || submitting">
              <span *ngIf="submitting" class="spinner-border spinner-border-sm me-2"></span>
              {{ editingId ? 'Update Mapping' : 'Save Mapping' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <div class="card shadow-sm border-0 mb-3">
      <div class="card-body">
        <div class="row g-3">
          <div class="col-md-4">
            <label class="form-label">Filter by Currency</label>
            <input class="form-control" [(ngModel)]="filterCurrencyCode" (ngModelChange)="applyFilters()"
              name="filterCurrencyCode" placeholder="CAD">
          </div>

          <div class="col-md-4">
            <label class="form-label">Filter by Provider</label>
            <input class="form-control" [(ngModel)]="filterProviderCode" (ngModelChange)="applyFilters()"
              name="filterProviderCode" placeholder="Flutterwave">
          </div>

          <div class="col-md-4">
            <label class="form-label">Filter by Workflow</label>
            <select class="form-select" [(ngModel)]="filterWorkflow" (ngModelChange)="applyFilters()" name="filterWorkflow">
              <option value="">All workflows</option>
              <option value="Deposit">Deposit</option>
              <option value="Withdrawal">Withdrawal</option>
            </select>
          </div>
        </div>
      </div>
    </div>

    <div class="card shadow-sm border-0">
      <div class="card-body">
        <div *ngIf="loading" class="text-muted">Loading mappings...</div>

        <div class="table-responsive" *ngIf="!loading">
          <table class="table align-middle">
            <thead>
              <tr>
                <th>Currency</th>
                <th>Provider</th>
                <th>Workflow</th>
                <th>Config</th>
                <th>Method</th>
                <th>Hint</th>
                <th>Priority</th>
                <th>Default</th>
                <th>Status</th>
                <th>Country</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let item of filteredMappings">
                <td>{{ item.currencyCode }}</td>
                <td>{{ item.providerName }}</td>
                <td>{{ item.workflow }}</td>
                <td>{{ item.gatewayConfigName }}</td>
                <td>{{ item.paymentMethod || '-' }}</td>
                <td>{{ item.frontendHint || '-' }}</td>
                <td>{{ item.priority }}</td>
                <td>
                  <span class="badge" [ngClass]="item.isDefault ? 'text-bg-primary' : 'text-bg-light'">
                    {{ item.isDefault ? 'Default' : 'No' }}
                  </span>
                </td>
                <td>
                  <span class="badge" [ngClass]="item.isActive ? 'text-bg-success' : 'text-bg-secondary'">
                    {{ item.isActive ? 'Active' : 'Inactive' }}
                  </span>
                </td>
                <td>{{ item.countryCode || '-' }}</td>
                <td>
                  <div class="d-flex flex-wrap gap-2">
                    <button class="btn btn-sm btn-outline-primary" type="button" (click)="toggleStatus(item)">
                      {{ item.isActive ? 'Deactivate' : 'Activate' }}
                    </button>
                    <button class="btn btn-sm btn-outline-secondary" type="button" (click)="edit(item)">Edit</button>
                    <button *ngIf="!item.isDefault" class="btn btn-sm btn-outline-primary" type="button" (click)="makeDefault(item)">
                      Set Default
                    </button>
                  </div>
                </td>
              </tr>

              <tr *ngIf="filteredMappings.length === 0">
                <td colspan="11" class="text-center text-muted">No mappings found.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <app-footer></app-footer>
  </main>
</div>
